/**
 * Modal d'édition d'un nœud de navigation (admin staff).
 *
 * Crée OU édite : si `node === null` on est en mode create, si node passé en
 * prop on hydrate les champs. Le parent est librement choisissable parmi les
 * nodes existants sauf le node lui-même et ses descendants (anti-cycle côté
 * front — backend devra aussi valider plus tard).
 *
 * Sources : multi-select des catégories POS détectées au runtime. Un parent
 * n'a typiquement pas de sources (les produits viennent des feuilles).
 */
import React, { useEffect, useMemo, useState } from 'react';
import './EditNavigationNodeSheet.css';
import { apiFetch } from '../../utils/api';

export interface SourceMappingInput {
  provider: string;
  ids: string[];
}

export interface NavigationNodeInput {
  id: string;
  parentId: string | null;
  label: string;
  emoji: string | null;
  description: string | null;
  position: number;
  sourceMappings: SourceMappingInput[];
  hidden: boolean;
}

export interface PosCategoryRef {
  provider: string;
  categoryId: string;
  categoryName: string;
  productCount: number;
}

interface Props {
  /** null si modal fermée, sinon mode create (avec parentId nullable) ou edit. */
  state:
    | { mode: 'create'; parentId: string | null; suggestedPosition?: number }
    | { mode: 'edit'; node: NavigationNodeInput }
    | null;
  /** Liste plate de tous les nodes (pour parent picker + filtrer descendants). */
  allNodes: NavigationNodeInput[];
  /** Cats POS disponibles au mapping. */
  posCategories: PosCategoryRef[];
  onClose: () => void;
  onSaved: () => void;
}

function descendantsOf(nodeId: string, all: NavigationNodeInput[]): Set<string> {
  const result = new Set<string>([nodeId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const n of all) {
      if (n.parentId && result.has(n.parentId) && !result.has(n.id)) {
        result.add(n.id);
        changed = true;
      }
    }
  }
  return result;
}

export function EditNavigationNodeSheet({ state, allNodes, posCategories, onClose, onSaved }: Props) {
  const [label, setLabel] = useState('');
  const [emoji, setEmoji] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  /** Set "provider::categoryId" pour fast toggle. */
  const [selectedCats, setSelectedCats] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  // Hydrate au changement de state
  useEffect(() => {
    if (!state) return;
    if (state.mode === 'edit') {
      const n = state.node;
      setLabel(n.label);
      setEmoji(n.emoji ?? '');
      setDescription(n.description ?? '');
      setParentId(n.parentId);
      setHidden(n.hidden);
      const keys = new Set<string>();
      for (const m of n.sourceMappings) {
        for (const id of m.ids) keys.add(`${m.provider}::${id}`);
      }
      setSelectedCats(keys);
    } else {
      setLabel('');
      setEmoji('');
      setDescription('');
      setParentId(state.parentId);
      setHidden(false);
      setSelectedCats(new Set());
    }
    setToast(null);
  }, [state]);

  // Body class TabBar masquée
  useEffect(() => {
    if (!state) return;
    document.body.classList.add('modal-sheet-open');
    return () => document.body.classList.remove('modal-sheet-open');
  }, [state]);

  // Parent picker : tous les nodes sauf le node lui-même + ses descendants
  const parentOptions = useMemo(() => {
    const exclude = state?.mode === 'edit' ? descendantsOf(state.node.id, allNodes) : new Set<string>();
    return allNodes.filter((n) => !exclude.has(n.id));
  }, [state, allNodes]);

  if (!state) return null;

  const toggleCat = (key: string) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const onSave = async () => {
    if (!label.trim()) {
      setToast({ kind: 'err', msg: 'Le libellé est requis' });
      return;
    }
    setSaving(true);
    setToast(null);
    const token = localStorage.getItem('alterrego_jwt');
    if (!token) {
      setToast({ kind: 'err', msg: 'Non connecté' });
      setSaving(false);
      return;
    }

    // Regroupe selectedCats par provider
    const byProvider = new Map<string, string[]>();
    for (const key of selectedCats) {
      const idx = key.indexOf('::');
      const provider = key.slice(0, idx);
      const id = key.slice(idx + 2);
      const arr = byProvider.get(provider) ?? [];
      arr.push(id);
      byProvider.set(provider, arr);
    }
    const sourceMappings: SourceMappingInput[] = Array.from(byProvider.entries()).map(
      ([provider, ids]) => ({ provider, ids }),
    );

    const body = {
      label: label.trim(),
      emoji: emoji.trim() || null,
      description: description.trim() || null,
      parentId,
      sourceMappings,
      hidden,
    };
    try {
      const url = state.mode === 'edit'
        ? `/pos/navigation/nodes/${state.node.id}`
        : `/pos/navigation/nodes`;
      const method = state.mode === 'edit' ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...body,
          position: state.mode === 'create' ? (state.suggestedPosition ?? 9999) : undefined,
        }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${txt.slice(0, 100)}`);
      }
      setToast({ kind: 'ok', msg: 'Enregistré' });
      onSaved();
      setTimeout(onClose, 400);
    } catch (err: any) {
      setToast({ kind: 'err', msg: err?.message ?? 'Erreur réseau' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ens-backdrop" onClick={onClose}>
      <div className="ens-sheet" onClick={(e) => e.stopPropagation()}>
        <button className="ens-close" onClick={onClose} aria-label="Fermer">×</button>
        <div className="ens-header">
          <h2>{state.mode === 'create' ? 'NOUVEAU NŒUD' : 'ÉDITER LE NŒUD'}</h2>
        </div>

        <div className="ens-section">
          <h3>Libellé</h3>
          <input
            type="text"
            className="ens-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ex : Vins rouges"
            maxLength={80}
          />
        </div>

        <div className="ens-section">
          <h3>Emoji</h3>
          <input
            type="text"
            className="ens-input ens-input-emoji"
            value={emoji}
            onChange={(e) => setEmoji(e.target.value)}
            placeholder="🍷"
            maxLength={4}
          />
          <p className="ens-hint">Optionnel — affiché à côté du libellé.</p>
        </div>

        <div className="ens-section">
          <h3>Description</h3>
          <textarea
            className="ens-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optionnel — affiché en sous-titre côté client."
            rows={2}
            maxLength={500}
          />
        </div>

        <div className="ens-section">
          <h3>Parent</h3>
          <select
            className="ens-select"
            value={parentId ?? ''}
            onChange={(e) => setParentId(e.target.value || null)}
          >
            <option value="">— Racine —</option>
            {parentOptions.map((n) => (
              <option key={n.id} value={n.id}>{n.emoji ? `${n.emoji} ` : ''}{n.label}</option>
            ))}
          </select>
        </div>

        <div className="ens-section">
          <h3>Catégories POS mappées <span className="ens-section-count">({selectedCats.size})</span></h3>
          <p className="ens-hint">Les produits de ces catégories apparaîtront sous ce nœud. Laisser vide pour un nœud parent (les produits viennent des feuilles).</p>
          <div className="ens-cat-list">
            {posCategories.map((c) => {
              const key = `${c.provider}::${c.categoryId}`;
              const active = selectedCats.has(key);
              return (
                <label key={key} className={`ens-cat-chip${active ? ' active' : ''}`}>
                  <input type="checkbox" checked={active} onChange={() => toggleCat(key)} />
                  <span>{c.categoryName} <span className="ens-cat-count">({c.productCount})</span></span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="ens-section">
          <label className="ens-toggle">
            <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
            <span>Masquer côté client</span>
          </label>
          <p className="ens-hint">Le nœud reste visible dans l'admin mais n'apparaît plus pour les visiteurs.</p>
        </div>

        {toast && <div className={`ens-toast ${toast.kind}`}>{toast.msg}</div>}

        <div className="ens-actions">
          <button className="ens-btn ens-btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="ens-btn ens-btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
